import { cartoTileUrl, cartoAttribution } from '../config/mapTiles';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { MapContainer, Marker, Pane, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MemoryThreads.css';

const defaultCenter = [32.047, 111.517];
const createId = () => Math.random().toString(36).substring(2, 10);

const initialLocationForm = {
  type: 'origin',
  label: '',
  timeframe: '',
  notes: '',
  coords: null,
  id: null
};

const initialThreadForm = {
  type: 'memory',
  locationId: '',
  title: '',
  detail: '',
  personStatus: 'connected'
};

const formatLatLng = (coords) => (coords ? `${coords.lat.toFixed(2)}°, ${coords.lng.toFixed(2)}°` : '等待地图拾取');

const buildPinIcon = (variant, isActive) => {
  const baseClass = `memoryThreads__pin memoryThreads__pin--${variant}${isActive ? ' memoryThreads__pin--active' : ''}`;
  const html = variant === 'current'
    ? `
        <div class="${baseClass}">
          <div class="memoryThreads__pin-visual">
            <div class="memoryThreads__pin-balloon">
              <span class="memoryThreads__pin-reflection"></span>
            </div>
            <svg class="memoryThreads__pin-string-svg" width="32" height="40" viewBox="0 0 32 40">
              <path d="M 16 0 Q 12 15 18 25 T 16 40" fill="none" stroke="rgba(176, 21, 52, 0.87)" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </div>
          <div class="memoryThreads__pin-shadow"></div>
        </div>
      `
    : `
        <div class="${baseClass}">
          <div class="memoryThreads__pin-visual">
            <div class="memoryThreads__pin-head"></div>
            <div class="memoryThreads__pin-body"></div>
          </div>
          <div class="memoryThreads__pin-shadow"></div>
        </div>
      `;

  const size = variant === 'current' ? [32, 70] : [20, 47];
  const anchor = variant === 'current' ? [16, 70] : [10, 47];

  return L.divIcon({
    className: 'memoryThreads__pinWrapper',
    html,
    iconSize: size,
    iconAnchor: anchor
  });
};

const getPinVerticalOffset = (variant) => {
  switch (variant) {
    case 'origin':
    case 'stay':
      return 37;
    case 'current':
      return 58;
    default:
      return 37;
  }
};

const MapClickHandler = ({ onPick }) => {
  useMapEvents({
    click(event) {
      const lat = Number(event.latlng.lat.toFixed(5));
      const lng = Number(event.latlng.lng.toFixed(5));
      onPick({ lat, lng });
    }
  });
  return null;
};

const ThreadCurvesOverlay = ({ connections }) => {
  const map = useMap();
  const [renderState, setRenderState] = useState({ box: null, paths: [], drops: [] });

  const recompute = useCallback(() => {
    if (!map || connections.length === 0) {
      setRenderState({ box: null, paths: [], drops: [] });
      return;
    }

    const pixelBounds = map.getPixelBounds();
    const topLeft = pixelBounds.min;
    const size = pixelBounds.getSize();
    const toSvgPoint = (point) => `${(point.x - topLeft.x).toFixed(1)} ${(point.y - topLeft.y).toFixed(1)}`;

    const paths = [];
    const drops = [];

    connections.forEach((connection, index) => {
      const rawStart = map.latLngToLayerPoint(connection.start);
      const rawEnd = map.latLngToLayerPoint(connection.end);
      const startPoint = new L.Point(rawStart.x, rawStart.y - (connection.startOffset || 0));
      const endPoint = new L.Point(rawEnd.x, rawEnd.y - (connection.endOffset || 0));
      if (!startPoint || !endPoint) return;

      const dx = Math.abs(startPoint.x - endPoint.x);
      const baseSag = connection.type === 'person'
        ? (connection.status === 'severed' ? 120 : 45)
        : 25;
      const sagFactor = connection.status === 'severed' ? 0.15 : 0.06;
      const sag = baseSag + dx * sagFactor;
      const tensionLift = connection.status === 'severed' ? -0.1 : 0.08;
      const ctrl1 = new L.Point(
        startPoint.x + (endPoint.x - startPoint.x) * (connection.type === 'person' ? 0.18 : 0.3),
        startPoint.y + (endPoint.y - startPoint.y) * (0.3 + tensionLift) + sag
      );
      const ctrl2 = new L.Point(
        startPoint.x + (endPoint.x - startPoint.x) * (connection.type === 'person' ? 0.82 : 0.7),
        startPoint.y + (endPoint.y - startPoint.y) * (0.7 - tensionLift) + sag
      );

      const d = `M ${toSvgPoint(startPoint)} C ${toSvgPoint(ctrl1)} ${toSvgPoint(ctrl2)} ${toSvgPoint(endPoint)}`;
      paths.push({
        id: connection.id,
        type: connection.type,
        status: connection.status,
        color: connection.color,
        d,
        delay: (index % 5) * 0.35
      });
    });

    setRenderState({
      box: { left: topLeft.x, top: topLeft.y, width: size.x, height: size.y },
      paths,
      drops
    });
  }, [connections, map]);

  React.useEffect(() => {
    if (!map) return undefined;
    recompute();
    map.on('move zoom resize', recompute);
    return () => {
      map.off('move zoom resize', recompute);
    };
  }, [map, recompute]);

  if (!renderState.box || renderState.paths.length === 0) return null;

  return (
    <Pane className="memoryThreads__threadsPane" name="threads">
      <svg
        className="memoryThreads__threadsSvg"
        width={renderState.box.width}
        height={renderState.box.height}
        style={{ transform: `translate(${renderState.box.left}px, ${renderState.box.top}px)` }}
      >
        {renderState.paths.map((path) => {
          const isSevered = path.status === 'severed';
          const strokeWidth = path.type === 'memory' ? 3.4 : 4.3;
          const strokeDasharray = isSevered ? '6 10' : 'none';
          const opacity = path.type === 'memory' ? 0.92 : (isSevered ? 0.7 : 1);

          return (
            <path
              key={path.id}
              d={path.d}
              fill="none"
              className={`memoryThreads__thread memoryThreads__thread--${path.type}${isSevered ? ' memoryThreads__thread--severed' : ''}`}
              style={{
                stroke: path.color,
                animationDelay: `${path.delay}s`,
                strokeWidth,
                strokeDasharray,
                opacity
              }}
            />
          );
        })}
        {renderState.drops.map((drop) => (
          <path
            key={drop.id}
            d={drop.d}
            fill="none"
            className="memoryThreads__thread memoryThreads__thread--drop"
            style={{ stroke: drop.color }}
          />
        ))}
      </svg>
    </Pane>
  );
};

function MemoryThreads() {
  const mapRef = useRef(null);
  const [locations, setLocations] = useState({ origin: null, current: null, stays: [] });
  const [memories, setMemories] = useState([]);
  const [people, setPeople] = useState([]);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [threadDialogOpen, setThreadDialogOpen] = useState(false);
  const [locationForm, setLocationForm] = useState(initialLocationForm);
  const [threadForm, setThreadForm] = useState(initialThreadForm);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [activeLocationId, setActiveLocationId] = useState('');
  const [locationFeedback, setLocationFeedback] = useState('');
  const [threadFeedback, setThreadFeedback] = useState('');

  const determineDefaultLocationType = () => {
    if (!locations.origin) return 'origin';
    if (!locations.current) return 'current';
    return 'stay';
  };

  const allNodes = useMemo(() => {
    const list = [];
    if (locations.origin) list.push({ ...locations.origin, type: 'origin' });
    locations.stays.forEach((stay) => list.push({ ...stay, type: 'stay' }));
    if (locations.current) list.push({ ...locations.current, type: 'current' });
    return list;
  }, [locations]);

  const locationOptions = useMemo(
    () => allNodes.filter((node) => node.type !== 'current' && node.coords),
    [allNodes]
  );

  const currentLabel = locations.current?.label || '现在';
  const archiveId = useMemo(() => {
    const seed = 8800 + allNodes.length * 7 + memories.length * 13 + people.length * 17;
    return `#${String(seed).padStart(4, '0')}`;
  }, [allNodes.length, memories.length, people.length]);

  const threadConnections = useMemo(() => {
    if (!locations.current?.coords) return [];
    const currentCoords = locations.current.coords;
    const nodesById = allNodes.reduce((acc, node) => {
      acc[node.id] = node;
      return acc;
    }, {});

    const connections = [];
    memories.forEach((memory) => {
      const originNode = nodesById[memory.locationId];
      if (!originNode?.coords) return;
      connections.push({
        id: memory.id,
        type: 'memory',
        status: 'connected',
        color: '#8c8c8c',
        start: originNode.coords,
        end: currentCoords,
        startOffset: getPinVerticalOffset(originNode.type || 'stay'),
        endOffset: getPinVerticalOffset('current')
      });
    });

    people.forEach((person) => {
      const originNode = nodesById[person.locationId];
      if (!originNode?.coords) return;
      connections.push({
        id: person.id,
        type: 'person',
        status: person.status,
        color: '#e93348',
        start: originNode.coords,
        end: currentCoords,
        startOffset: getPinVerticalOffset(originNode.type || 'stay'),
        endOffset: getPinVerticalOffset('current')
      });
    });

    return connections;
  }, [locations, memories, people, allNodes]);

  const handleDownloadMap = useCallback(() => {
    if (mapRef.current === null) {
      return;
    }

    toPng(mapRef.current, { cacheBust: true, backgroundColor: '#ffffff' })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = 'memory-map.png';
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Failed to download map image', err);
      });
  }, [mapRef]);

  const handleMapClick = (coords) => {
    setLocationFeedback('');
    setLocationForm((prev) => ({
      ...(locationDialogOpen ? prev : { ...initialLocationForm, type: determineDefaultLocationType() }),
      coords
    }));
    if (!locationDialogOpen) {
      setLocationDialogOpen(true);
    }
  };

  const handleSelectLocation = (node) => {
    if (!node.coords) return;
    setActiveLocationId(node.id);
    setMapCenter([node.coords.lat, node.coords.lng]);
  };

  const handleMarkerClick = (node) => {
    handleSelectLocation(node);
    openLocationDialog(node);
  };

  const openLocationDialog = (preset) => {
    if (preset) {
      setLocationForm({
        type: preset.type,
        label: preset.label,
        timeframe: preset.timeframe || '',
        notes: preset.notes || '',
        coords: preset.coords ? { ...preset.coords } : null,
        id: preset.id
      });
    } else {
      setLocationForm({ ...initialLocationForm, type: determineDefaultLocationType() });
    }
    setLocationFeedback('');
    setLocationDialogOpen(true);
  };

  const openThreadDialog = () => {
    setThreadForm((prev) => ({
      ...initialThreadForm,
      locationId: locationOptions[0]?.id || ''
    }));
    setThreadFeedback('');
    setThreadDialogOpen(true);
  };

  const upsertLocation = (payload) => {
    if (!payload.coords) {
      setLocationFeedback('请先在左侧地图上点选坐标。');
      return false;
    }
    if (!payload.label.trim()) {
      setLocationFeedback('请为此落点写一个名字。');
      return false;
    }

    const enriched = { ...payload, id: payload.id || createId() };
    setLocations((prev) => {
      if (enriched.type === 'origin') {
        return { ...prev, origin: enriched };
      }
      if (enriched.type === 'current') {
        return { ...prev, current: enriched };
      }
      const stays = prev.stays.filter((stay) => stay.id !== enriched.id);
      return { ...prev, stays: [...stays, enriched] };
    });
    setActiveLocationId(enriched.id);
    setMapCenter([enriched.coords.lat, enriched.coords.lng]);
    return true;
  };

  const handleLocationSubmit = (event) => {
    event.preventDefault();
    if (upsertLocation(locationForm)) {
      setLocationDialogOpen(false);
      setLocationForm({ ...initialLocationForm, type: determineDefaultLocationType() });
    }
  };

  const handleThreadSubmit = (event) => {
    event.preventDefault();
    if (!locations.current) {
      setThreadFeedback('请先记录「现在」的红色气球。');
      return;
    }
    if (!threadForm.locationId) {
      setThreadFeedback('请选择一个落点来连线。');
      return;
    }
    if (!threadForm.title.trim()) {
      setThreadFeedback('请为记忆或人物写个名字。');
      return;
    }

    const payload = {
      id: createId(),
      locationId: threadForm.locationId,
      title: threadForm.title,
      detail: threadForm.detail
    };

    if (threadForm.type === 'memory') {
      setMemories((prev) => [...prev, payload]);
    } else {
      setPeople((prev) => [...prev, { ...payload, status: threadForm.personStatus }]);
    }

    setThreadForm((prev) => ({
      ...initialThreadForm,
      type: prev.type,
      locationId: prev.locationId
    }));
    setThreadDialogOpen(false);
  };

  const togglePersonStatus = (id) => {
    setPeople((prev) => prev.map((person) => (person.id === id
      ? { ...person, status: person.status === 'severed' ? 'connected' : 'severed' }
      : person)));
  };

  const experiences = memories.map((memory) => ({
    ...memory,
    origin: allNodes.find((node) => node.id === memory.locationId)
  }));

  const connectedPeople = people
    .filter((person) => person.status === 'connected')
    .map((person) => ({
      ...person,
      origin: allNodes.find((node) => node.id === person.locationId)
    }));

  const disconnectedPeople = people
    .filter((person) => person.status === 'severed')
    .map((person) => ({
      ...person,
      origin: allNodes.find((node) => node.id === person.locationId)
    }));

  return (
    <Box className="memoryThreads">

      <div className="memoryThreads__mainSection">
        <div className="memoryThreads__mapShell" ref={mapRef}>
          <MapContainer center={mapCenter} zoom={3} minZoom={2} scrollWheelZoom className="memoryThreads__mapContainer">
            <TileLayer
              url={cartoTileUrl('light_all', true)}
                        attribution={cartoAttribution}
              crossOrigin="anonymous"
            />
            {allNodes.map((node) => (
              node.coords ? (
                <Marker
                  key={node.id}
                  position={[node.coords.lat, node.coords.lng]}
                  icon={buildPinIcon(node.type, node.id === activeLocationId)}
                  eventHandlers={{
                    click: () => handleMarkerClick(node)
                  }}
                />
              ) : null
            ))}
            <ThreadCurvesOverlay connections={threadConnections} />
            <MapClickHandler onPick={handleMapClick} />
          </MapContainer>
          <div className="memoryThreads__mapOverlay">
            <div>
              <p>所有落点</p>
              <Typography variant="h5">{allNodes.length || '00'}</Typography>
            </div>
            <div>
              <p>编织记忆</p>
              <Typography variant="h5">{memories.length + people.length}</Typography>
            </div>
          </div>
          {locationDialogOpen && (
            <div className="memoryThreads__mapHint">正在添加落点 · 点击地图以拾取坐标</div>
          )}
        </div>

        <div className="memoryThreads__controlPanel">
          <div className="memoryThreads__panelHeader">
            <h2 className="memoryThreads__panelTitle">我的足迹</h2>
            <div className="memoryThreads__panelActions">
              <Button variant="outlined" size="small" onClick={() => openLocationDialog()}>
                + 地点
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={openThreadDialog}
                disabled={!locations.current || locationOptions.length === 0}
                sx={{ bgcolor: '#0f0f10', '&:hover': { bgcolor: '#333' } }}
              >
                + 连线
              </Button>
            </div>
          </div>

          <div className="memoryThreads__locationsList">
            {allNodes.length === 0 && (
              <div className="memoryThreads__emptyState">
                <p>暂无落点记录</p>
              </div>
            )}
            {allNodes.map((node) => (
              <div
                key={node.id}
                className={`memoryThreads__locationItem${node.id === activeLocationId ? ' is-active' : ''}`}
                onClick={() => handleSelectLocation(node)}
              >
                <div className="memoryThreads__locationInfo">
                  <strong>{node.label}</strong>
                  <span>{node.timeframe || '未填写时间'}</span>
                </div>
                <div className="memoryThreads__locationMeta">
                  <Chip
                    label={node.type === 'origin' ? '黑钉' : node.type === 'current' ? '红气球' : '停留'}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Button size="small" sx={{ minWidth: 0, p: 0.5 }} onClick={(event) => { event.stopPropagation(); openLocationDialog(node); }}>
                    编辑
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="memoryThreads__storiesSection">
        <div className="memoryThreads__storiesHeader">
          <h3 className="memoryThreads__storiesTitle">记忆档案</h3>
          <div className="memoryThreads__legendBar">
            <span><i className="memoryThreads__dot memoryThreads__dot--origin"></i>出生地</span>
            <span><i className="memoryThreads__dot memoryThreads__dot--stay"></i>停留地</span>
            <span><i className="memoryThreads__dot memoryThreads__dot--current"></i>现在</span>
            <span><i className="memoryThreads__line memoryThreads__line--memory"></i>经历</span>
            <span><i className="memoryThreads__line memoryThreads__line--person"></i>人物</span>
            <Button variant="text" size="small" onClick={handleDownloadMap} sx={{ ml: 2, color: '#666' }}>
              下载地图
            </Button>
          </div>
        </div>

        <div className="memoryThreads__splitView">
          <div className="memoryThreads__splitColumn">
            <h4 className="memoryThreads__columnTitle">散落的经历</h4>
            <div className="memoryThreads__scrollArea">
              {experiences.length === 0 && (
                <div className="memoryThreads__emptyGrid">
                  <p>还没有任何经历连线。</p>
                </div>
              )}
              {experiences.map((memory) => (
                <div key={memory.id} className="memoryThreads__card memoryThreads__card--experience">
                  <div className="memoryThreads__cardHeader">
                    <span className="memoryThreads__cardType">EXPERIENCE</span>
                    <span className="memoryThreads__cardRoute">{memory.origin?.label} → {currentLabel}</span>
                  </div>
                  <h4 className="memoryThreads__cardTitle">{memory.title || '未命名经历'}</h4>
                  <blockquote>“{memory.detail || '这是一段等待书写的故事。'}”</blockquote>
                </div>
              ))}
            </div>
          </div>

          <div className="memoryThreads__splitColumn">
            <h4 className="memoryThreads__columnTitle">散落的人</h4>
            <div className="memoryThreads__scrollArea">
              {connectedPeople.length === 0 && disconnectedPeople.length === 0 && (
                <div className="memoryThreads__emptyGrid">
                  <p>还没有任何人物连线。</p>
                </div>
              )}
              {connectedPeople.map((person) => (
                <div key={person.id} className="memoryThreads__card memoryThreads__card--connection">
                  <div className="memoryThreads__cardHeader">
                    <span className="memoryThreads__cardType">CONNECTION</span>
                    <span className="memoryThreads__cardRoute">{person.origin?.label} → {currentLabel}</span>
                  </div>
                  <h4 className="memoryThreads__cardTitle">{person.title || '未命名人物'}</h4>
                  <blockquote>“{person.detail || '她/他在旅途中留下了痕迹。'}”</blockquote>
                  <div className="memoryThreads__cardActions">
                    <Button size="small" color="error" onClick={() => togglePersonStatus(person.id)}>标记断裂</Button>
                  </div>
                </div>
              ))}

              {disconnectedPeople.map((person) => (
                <div key={person.id} className="memoryThreads__card memoryThreads__card--disconnected">
                  <div className="memoryThreads__cardHeader">
                    <span className="memoryThreads__cardType">DISCONNECTED</span>
                    <span className="memoryThreads__cardRoute">{person.origin?.label} → {currentLabel}</span>
                  </div>
                  <h4 className="memoryThreads__cardTitle">{person.title || '未命名人物'}</h4>
                  <blockquote>“{person.detail || '这段关系垂落在记忆里。'}”</blockquote>
                  <div className="memoryThreads__cardActions">
                    <Button size="small" onClick={() => togglePersonStatus(person.id)}>恢复连线</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={locationDialogOpen} onClose={() => setLocationDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>记录一个落点</DialogTitle>
        <form onSubmit={handleLocationSubmit}>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              在左侧地图点击以拾取坐标，再填写以下信息。
            </Typography>
            <TextField
              select
              label="落点类型"
              fullWidth
              value={locationForm.type}
              onChange={(event) => setLocationForm((prev) => ({ ...prev, type: event.target.value }))}
              sx={{ mb: 2 }}
            >
              <MenuItem value="origin" disabled={Boolean(locations.origin) && locationForm.type !== 'origin'}>
                出生地 · 黑钉
              </MenuItem>
              <MenuItem value="stay">停留地 · 灰钉</MenuItem>
              <MenuItem value="current" disabled={Boolean(locations.current) && locationForm.type !== 'current'}>
                现在 · 红气球
              </MenuItem>
            </TextField>
            <TextField
              label="地点名称"
              fullWidth
              value={locationForm.label}
              onChange={(event) => setLocationForm((prev) => ({ ...prev, label: event.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              label="时间范围"
              placeholder="例如：2003 - 2007"
              fullWidth
              value={locationForm.timeframe}
              onChange={(event) => setLocationForm((prev) => ({ ...prev, timeframe: event.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              label="注记"
              multiline
              minRows={3}
              fullWidth
              value={locationForm.notes}
              onChange={(event) => setLocationForm((prev) => ({ ...prev, notes: event.target.value }))}
              sx={{ mb: 2 }}
            />
            <Typography variant="caption" color={locationForm.coords ? 'text.secondary' : 'error'}>
              {locationForm.coords ? `坐标：${formatLatLng(locationForm.coords)}` : '尚未选中坐标'}
            </Typography>
            {locationFeedback && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {locationFeedback}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLocationDialogOpen(false)}>取消</Button>
            <Button type="submit" variant="contained">保存落点</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={threadDialogOpen} onClose={() => setThreadDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>编织一条记忆/人物</DialogTitle>
        <form onSubmit={handleThreadSubmit}>
          <DialogContent dividers>
            <TextField
              select
              label="连线类型"
              fullWidth
              value={threadForm.type}
              onChange={(event) => setThreadForm((prev) => ({ ...prev, type: event.target.value }))}
              sx={{ mb: 2 }}
            >
              <MenuItem value="memory">经历 · 灰毛线</MenuItem>
              <MenuItem value="person">人物 · 红毛线</MenuItem>
            </TextField>
            <TextField
              select
              label="从哪个落点出发"
              fullWidth
              value={threadForm.locationId}
              onChange={(event) => setThreadForm((prev) => ({ ...prev, locationId: event.target.value }))}
              sx={{ mb: 2 }}
            >
              {locationOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label} · {option.timeframe || '未填写时间'}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={threadForm.type === 'memory' ? '经历标题' : '人物姓名'}
              fullWidth
              value={threadForm.title}
              onChange={(event) => setThreadForm((prev) => ({ ...prev, title: event.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              label="描述或注解"
              multiline
              minRows={3}
              fullWidth
              value={threadForm.detail}
              onChange={(event) => setThreadForm((prev) => ({ ...prev, detail: event.target.value }))}
              sx={{ mb: 2 }}
            />
            {threadForm.type === 'person' && (
              <TextField
                select
                label="目前关系状态"
                fullWidth
                value={threadForm.personStatus}
                onChange={(event) => setThreadForm((prev) => ({ ...prev, personStatus: event.target.value }))}
              >
                <MenuItem value="connected">仍在相连</MenuItem>
                <MenuItem value="severed">已经断裂</MenuItem>
              </TextField>
            )}
            {threadFeedback && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {threadFeedback}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setThreadDialogOpen(false)}>取消</Button>
            <Button type="submit" variant="contained">保存连线</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default MemoryThreads;
